/**
 * ReferralCampaignProgress Component
 *
 * Displays VIP referral campaign progress information:
 * - Shows the referral code name (e.g., "BIRDI Bonus") prominently
 * - "Earned X/20,000 Points" progress
 * - "2x Points Active" badge
 *
 * Only visible when user is eligible for the campaign (bound to VIP referral code).
 * Designed to be subtle but clearly visible.
 */

import { useReferralCampaign } from '@/hooks/useReferralCampaign';

export function ReferralCampaignProgress() {
  const { campaignData, isLoading } = useReferralCampaign();

  // Don't show if loading or not eligible
  if (isLoading || !campaignData?.isEligible) {
    return null;
  }

  const progressPercentage = (campaignData.userBonusEarned / campaignData.maxBonus) * 100;

  // Show the code name prominently (e.g., "BIRDI Bonus" instead of generic "VIP Referral Bonus")
  const bonusLabel = campaignData.boundReferralCode
    ? `${campaignData.boundReferralCode} Bonus`
    : 'VIP Referral Bonus';

  return (
    <div
      className="flex flex-col gap-2 px-4 py-3 rounded-lg"
      style={{
        backgroundColor: 'rgba(139, 92, 246, 0.08)',
        border: '1px solid rgba(139, 92, 246, 0.3)',
        fontFamily: 'Outfit, sans-serif',
      }}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span style={{ color: '#A78BFA', fontSize: '13px', fontWeight: 600 }}>{bonusLabel}</span>
          <span
            className="px-2 py-0.5 rounded text-xs font-semibold"
            style={{
              backgroundColor: 'rgba(139, 92, 246, 0.2)',
              color: '#A78BFA',
            }}
          >
            2x ACTIVE
          </span>
        </div>
      </div>

      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between gap-2">
          <span style={{ color: '#A78BFA', fontWeight: 600, fontSize: '14px' }}>
            {campaignData.userBonusEarned.toLocaleString()}/{campaignData.maxBonus.toLocaleString()} points
          </span>
          <span style={{ color: '#666', fontSize: '11px' }}>
            {campaignData.remainingBonus.toLocaleString()} remaining
          </span>
        </div>

        {/* Progress bar */}
        <div
          className="w-full h-1.5 rounded-full overflow-hidden"
          style={{ backgroundColor: 'rgba(139, 92, 246, 0.15)' }}
        >
          <div
            className="h-full transition-all duration-300 rounded-full"
            style={{
              width: `${Math.min(progressPercentage, 100)}%`,
              backgroundColor: '#A78BFA',
            }}
          />
        </div>
      </div>

      <div style={{ color: '#666', fontSize: '10px' }}>
        Earn 2x bonus points on chest opening prizes
      </div>
    </div>
  );
}
